import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';

// Job reminders (owner 2026-08-16) — the operator configures the WHOLE email (to/cc/subject/body),
// the deadline and how many days ahead to send; a daily 08:10 Bangkok sweep sends it automatically.
// Visas need 1-2 months of processing time, so the lead defaults to 45 days. Jobs without a
// reminder send nothing. The job lead can re-open and edit any reminder later.

type Reminder = {
  id: string; title: string; due_date: string; lead_days: number; repeat: string;
  email_to: string[]; email_cc: string[]; email_subject: string; email_body: string;
  active: number; last_sent_at: number; last_sent_for: string; send_date: string;
};

const inp: React.CSSProperties = { width: '100%', padding: '7px 9px', fontSize: 12.5, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 8, color: 'var(--text-primary)', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', display: 'block', margin: '8px 0 3px' };
const REPEAT_LABELS: Record<string, string> = { none: 'One-off', monthly: 'Every month', quarterly: 'Every 3 months', yearly: 'Every year' };

function Editor({ jobId, initial, defaults, onClose, onSaved }: {
  jobId: string; initial: Reminder | null;
  defaults: { title: string; to: string[]; subject: string; body: string; leadDays?: number };
  onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState(() => initial ? {
    title: initial.title, dueDate: initial.due_date, leadDays: String(initial.lead_days), repeat: initial.repeat,
    to: initial.email_to.join(', '), cc: initial.email_cc.join(', '),
    subject: initial.email_subject, body: initial.email_body, active: !!initial.active,
  } : {
    title: defaults.title, dueDate: '', leadDays: String(defaults.leadDays || 45), repeat: 'none',
    to: defaults.to.join(', '), cc: '', subject: defaults.subject, body: defaults.body, active: true,
  });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const set = (k: string) => (e: any) => setF(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    const payload = { title: f.title, dueDate: f.dueDate, leadDays: Number(f.leadDays), repeat: f.repeat, to: f.to, cc: f.cc, subject: f.subject, body: f.body, active: f.active };
    try {
      if (initial) await api.jobReminderUpdate(jobId, initial.id, payload);
      else await api.jobReminderCreate(jobId, payload);
      onSaved(); onClose();
    } catch (e: any) { setErr(e?.message || 'Save failed'); setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div style={{ width: 540, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 14.5, fontWeight: 800 }}><i className="ti ti-bell" style={{ marginRight: 6 }} />{initial ? 'Edit reminder' : 'New reminder'}</div>
        <label style={lbl}>What is this reminder for?</label>
        <input value={f.title} onChange={set('title')} placeholder="e.g. Non-B visa expiry" style={inp} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <div><label style={lbl}>Deadline (due date)</label><input type="date" value={f.dueDate} onChange={set('dueDate')} style={inp} /></div>
          <div><label style={lbl}>Send N days BEFORE</label><input type="number" min={0} max={365} value={f.leadDays} onChange={set('leadDays')} style={inp} /></div>
          <div><label style={lbl}>Repeat</label>
            <select value={f.repeat} onChange={set('repeat')} style={inp}>
              {Object.entries(REPEAT_LABELS).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select></div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>Visas usually need 30–60 days of lead time. {f.dueDate && Number(f.leadDays) >= 0 ? `This email will go out on ${addDays(f.dueDate, -Number(f.leadDays || 0))}.` : ''}</div>
        <label style={lbl}>Email To (comma-separated)</label>
        <input value={f.to} onChange={set('to')} placeholder="client@example.com" style={inp} />
        <label style={lbl}>Cc</label>
        <input value={f.cc} onChange={set('cc')} placeholder="office@…" style={inp} />
        <label style={lbl}>Subject</label>
        <input value={f.subject} onChange={set('subject')} style={inp} />
        <label style={lbl}>Body — placeholders: {'{{dueDate}} {{daysLeft}} {{jobNo}} {{title}}'}</label>
        <textarea value={f.body} onChange={set('body')} rows={7} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.active} onChange={e => setF(p => ({ ...p, active: e.target.checked }))} />Active — will send automatically
        </label>
        {err && <div style={{ marginTop: 8, fontSize: 12, color: '#E24B4A' }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={() => !busy && onClose()} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={busy} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? '…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

export default function JobReminders({ jobId, jobNo, specificJob, clientEmail, onChanged, readOnly }: { jobId: string; jobNo: number; specificJob: string; clientEmail: string; onChanged?: () => void; readOnly?: boolean }) {
  const [rows, setRows] = useState<Reminder[]>([]);
  const [edit, setEdit] = useState<Reminder | null | 'new'>(null);
  // Service-matched template (Calem 2026-08-17): 90-Day=15d lead + passport + 400 THB; visa
  // extensions/work permit/licenses each carry their own wording and price-list fees. Loaded once
  // per job; the editor pre-fills from it and everything stays editable.
  const [tpl, setTpl] = useState<any>(null);
  const load = () => { api.jobReminders(jobId).then((r: any) => { setRows(r.rows || []); onChanged?.(); }).catch(() => setRows([])); };
  useEffect(() => { load(); api.jobReminderTemplate(jobId).then(setTpl).catch(() => setTpl(null)); }, [jobId]); // eslint-disable-line

  const defaults = {
    title: tpl?.title || specificJob || '',
    leadDays: tpl?.leadDays,
    to: clientEmail ? [clientEmail] : [],
    subject: tpl?.subject || `Reminder: ${specificJob || 'upcoming deadline'} — due {{dueDate}}`,
    body: tpl?.body || `Dear Sir/Madam,\n\nThis is a friendly reminder that ${specificJob || 'your matter'} (Job #${Number(jobNo).toLocaleString()}) is due on {{dueDate}} — {{daysLeft}} day(s) from today.\n\nPlease contact us so we can arrange the renewal/processing in good time.\n\nKind regards`,
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.03em' }}>Reminders</label>
        {readOnly
          ? <span style={{ marginLeft: 'auto', fontSize: 10.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>read-only — set the job to "4. Complete" to change</span>
          : <button onClick={() => setEdit('new')} style={{ marginLeft: 'auto', padding: '3px 10px', borderRadius: 8, border: '1px dashed var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
              <i className="ti ti-bell-plus" style={{ marginRight: 4 }} aria-hidden />Add reminder
            </button>}
      </div>
      {!rows.length && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '4px 0' }}>No reminders — this job will not send any automatic emails.</div>}
      {rows.map(r => {
        const sent = r.last_sent_for === r.due_date && r.repeat === 'none';
        return (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', marginTop: 6, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', opacity: r.active ? 1 : 0.55 }}>
            <i className="ti ti-bell" style={{ color: r.active ? '#B45309' : 'var(--text-tertiary)' }} aria-hidden />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || '(untitled)'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                Due {r.due_date} · emails {r.lead_days}d before ({r.send_date}) · {REPEAT_LABELS[r.repeat] || r.repeat} · to {r.email_to.join(', ') || '—'}
                {sent ? ' · ✓ sent' : r.last_sent_at ? ` · last sent ${new Date(r.last_sent_at * 1000).toISOString().slice(0, 10)}` : ''}
              </div>
            </div>
            {!r.active && <span style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text-tertiary)', border: '1px solid var(--border-strong)', borderRadius: 999, padding: '1px 7px' }}>OFF</span>}
            {!readOnly && <button onClick={() => setEdit(r)} title="Edit" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 2 }}><i className="ti ti-pencil" aria-hidden /></button>}
            {!readOnly && <button onClick={async () => { if (window.confirm('Delete this reminder? No email will be sent.')) { await api.jobReminderDelete(jobId, r.id); load(); } }} title="Delete" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#c0453f', padding: 2 }}><i className="ti ti-trash" aria-hidden /></button>}
          </div>
        );
      })}
      {edit && <Editor jobId={jobId} initial={edit === 'new' ? null : edit} defaults={defaults} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
